// src/pages/SimpleMenuPage.tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, getFullImageUrl } from '../lib/api';
import { Product, Menu, PublicMenuResponse, SubProduct, TenantWithInfo, TenantConfig, TenantInfo, ConfigResponse, Offer } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AboutUsSection } from '../components/AboutUsSection';
import { useAuthStore } from '../stores/useAuthStore';
import { useCartStore } from '../stores/useCartStore';
import { useClientUnpaidOrders, useClientAcceptedOrders } from '../hooks/queries/useOrders';
import { Search, ShoppingCart, X, Plus, Minus, Trash2, Clock, ChevronUp, FileText, Sparkles } from 'lucide-react';
import { formatPreparationTime } from '../lib/format';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { OffersCarousel } from '../components/OffersCarousel';
import { OfferDetailModal } from '../components/OfferDetailModal';
import { useOffers } from '../hooks/queries/useMenu';
import KIERO_LOGO from '../media/KIERO.png';


export function SimpleMenuPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [data, setData] = useState<PublicMenuResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
    
    // ... (rest of the component state)
    const [cartOpen, setCartOpen] = useState(false);
    const [tenantUuid, setTenantUuid] = useState<string | null>(null);
    const [config, setConfig] = useState<TenantConfig | null>(null);
    const [tenantAbout, setTenantAbout] = useState<TenantWithInfo | null>(null);

    const { identifier, validateSession: authValidateSession, tenant: storeTenant } = useAuthStore();
    const { items: cart, offers: cartOffers, addItem, addOffer, updateQuantity, updateOfferQuantity, removeItem: removeFromCart, removeOffer, getSubtotal } = useCartStore();

    // Data de tracking
    const { data: unpaidOrdersData } = useClientUnpaidOrders();
    const { data: acceptedOrdersData } = useClientAcceptedOrders();
    const { data: offers } = useOffers();

    // Mapear ordenes deduplicando por uuid (un pedido puede aparecer en ambas listas)
    const uniqueOrdersMap = new Map();
    [...(unpaidOrdersData?.orders || []), ...(acceptedOrdersData?.orders || [])].forEach((order: any) => {
        if (order.uuid) uniqueOrdersMap.set(order.uuid, order);
    });
    const allOrders = Array.from(uniqueOrdersMap.values())
        .sort((a: any, b: any) => new Date(b.created_at || new Date()).getTime() - new Date(a.created_at || new Date()).getTime());


    // Inicializar sesión y cargar menú
    useEffect(() => {
        if (!token) {
            setError('Token de acceso no proporcionado');
            setLoading(false);
            return;
        }

        const init = async () => {
            try {
                // Validar JWT para obtener session_token y tenantUuid usando el store
                const session = await authValidateSession(identifier ?? 'anonymous', token);
                
                if (!session || !apiClient.getSessionToken()) {
                    setError('Token de acceso inválido o expirado.');
                    setLoading(false);
                    return;
                }

                setTenantUuid(useAuthStore.getState().tenantUuid);

                // Cargar config del tenant
                let tenantData = null;
                const configResp = await apiClient.getConfig() as ConfigResponse;
                if (configResp?.success) {
                    const configData = configResp.config;
                    const tenantInfo = configResp.tenant || {} as TenantInfo;
                    setConfig(configData);
                    setTenantAbout({
                        ...configData,
                        name: configData.tenant_name || 'ComandaYa',
                        description: configData.business_description || '',
                        logotype: configData.logotype || '',
                        banner: configData.banner || '',
                        about_us: tenantInfo.about_us || configData.business_description || null,
                        instagram: tenantInfo.instagram || null,
                        facebook: tenantInfo.facebook || null,
                        tiktok: tenantInfo.tiktok || null,
                        whatsapp: tenantInfo.whatsapp || null,
                    });
                    tenantData = {
                        name: configData.tenant_name || 'ComandaYa',
                        description: configData.business_description || '',
                        logotype: configData.logotype || '',
                        banner: configData.banner || ''
                    };
                }

                // Cargar menús
                try {
                    const privateMenus = await apiClient.getMenus();
                    if (Array.isArray(privateMenus)) {
                        setData({
                            success: true,
                            tenant: tenantData || {
                                name: 'Restaurante',
                                description: '',
                                logotype: '',
                                banner: ''
                            },
                            menus: privateMenus
                        });
                    } else {
                        setError('Formato de menú inválido.');
                    }
                } catch (menuErr: any) {
                    setError('No se pudo cargar el menú.');
                }
            } catch (err: any) {
                console.error('Error inicializando SimpleMenuPage:', err);
                setError(err.message || 'Error al conectar con el servidor.');
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [token, identifier, authValidateSession]);

    const totalItems = useCartStore.getState().getItemCount();
    const totalPrice = getSubtotal();
    const currencySymbol = config?.currency_symbol || '$';

    const isMenuTimeValid = (menu: Menu): boolean => {
        if (!menu.schedule) return true;
        const dayMap = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
        const now = new Date();
        const currentDayIndex = now.getDay();
        const prevDayIndex = currentDayIndex === 0 ? 6 : currentDayIndex - 1;

        const getScheduleForDay = (dayIndex: number) => {
            const schedule = menu.schedule!;
            const key = dayMap[dayIndex];
            return schedule[key] || schedule[key.normalize("NFD").replace(/[\u0300-\u036f]/g, "")];
        };

        const checkSchedule = (schedule: any, isPrevDay: boolean) => {
            if (!schedule) return false;
            let start = "", end = "";
            if (typeof schedule === 'string') {
                const parts = schedule.split('-');
                if (parts.length === 2) { start = parts[0]; end = parts[1]; }
            } else if (typeof schedule === 'object') {
                const ts = schedule as any;
                if (ts.time) { start = ts.time.start; end = ts.time.end; }
                else if (ts.start && ts.end) { start = ts.start; end = ts.end; }
            }
            if (!start || !end) return true;

            const current = now.getHours() * 60 + now.getMinutes();
            const [sH, sM] = start.split(':').map(Number);
            const [eH, eM] = end.split(':').map(Number);
            const startT = sH * 60 + sM;
            const endT = eH * 60 + eM;

            if (isPrevDay) {
                if (endT < startT) {
                    return current <= endT;
                }
                return false;
            } else {
                if (endT < startT) {
                    return current >= startT || current <= endT;
                }
                return current >= startT && current <= endT;
            }
        };

        if (checkSchedule(getScheduleForDay(currentDayIndex), false)) return true;
        if (checkSchedule(getScheduleForDay(prevDayIndex), true)) return true;

        return false;
    };

    const handleProceedToPayment = () => {
        if ((cart.length === 0 && cartOffers.length === 0) || !token) return;
        navigate(`/simple-payment/${token}`, {
            state: {
                cart,
                cartOffers,
                totalPrice,
                tenantUuid,
                config,
            }
        });
    };

    if (loading) return <LoadingSpinner message="Preparando el menú..." />;
    if (error) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            <div className="text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Error al cargar</h2>
                <p className="text-gray-500">{error}</p>
            </div>
        </div>
    );
    if (!data) return null;

    const { tenant, menus } = data;

    const filteredMenus = activeCategory === 'all'
        ? menus
        : menus.filter((m: Menu) => m.id.toString() === activeCategory);

    const allProducts: { product: Product; menu: Menu }[] = [];
    filteredMenus.forEach((m: Menu) => {
        m.products.forEach((p: Product) => {
            const isDuplicate = allProducts.some(ap => ap.product.uuid === p.uuid);
            const matchesSearch = (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (p.description || '').toLowerCase().includes(searchQuery.toLowerCase());
            if (!isDuplicate && matchesSearch) {
                allProducts.push({ product: p, menu: m });
            }
        });
    });

    return (
        <div className="min-h-screen pb-32" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            {/* Header Banner */}
            <div className="relative h-52 overflow-hidden">
                {tenant.banner ? (
                    <img src={getFullImageUrl(tenant.banner)} alt={tenant.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, var(--color-primary-400), var(--color-primary-600))' }} />
                )}
                <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                    <div className="w-20 h-20 rounded-[1.75rem] bg-white p-1 shadow-2xl mb-3 overflow-hidden border-4 border-white/20">
                        <img src={tenant.logotype ? getFullImageUrl(tenant.logotype) : (storeTenant?.partner_branding === 'KIERO' ? KIERO_LOGO : undefined)} alt={tenant.name} className="w-full h-full object-contain rounded-[1.5rem]" />
                    </div>
                    <h1 className="text-2xl font-bold text-white drop-shadow-lg mb-1">{tenant.name}</h1>
                    <p className="text-white/80 text-xs font-medium max-w-xs line-clamp-2 whitespace-pre-line">{tenant.description}</p>
                </div>
            </div>

            {/* Offers Carousel */}
            <div className="px-4 py-3">
                <OffersCarousel offers={offers || []} onOfferClick={(offer) => setSelectedOffer(offer)} />
            </div>

            {/* Search + Categories */}
            <div className="sticky top-0 z-30">
                <div className="backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 shadow-sm" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                    <div className="px-4 py-3 flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="¿Qué estás buscando hoy?"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-100/70 dark:bg-gray-800/50 border border-gray-200/20 dark:border-white/5 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-accent-500/50 outline-none text-sm"
                                style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                            />
                        </div>
                        {identifier && allOrders.length > 0 && (
                            <button
                                onClick={() => navigate(`/simple-dashboard/${token}`)}
                                className="flex-shrink-0 relative flex items-center justify-center px-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-xl text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <FileText className="w-5 h-5" />
                                <span className="absolute -top-1.5 -right-1.5 bg-accent-500 text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: 'var(--color-accent-500)' }}>
                                    {allOrders.length}
                                </span>
                            </button>
                        )}
                    </div>
                    <div className="px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
                        {menus.map((menu: Menu) => (
                            <button
                                key={menu.id}
                                onClick={() => setActiveCategory(menu.id.toString())}
                                className={`px-4 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${activeCategory === menu.id.toString() ? 'text-white shadow-md' : 'bg-gray-100/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400'}`}
                                style={activeCategory === menu.id.toString() ? { backgroundColor: 'var(--color-accent-500)' } : {}}
                            >{menu.name}</button>
                        ))}
                        <button
                            onClick={() => setActiveCategory('all')}
                            className={`px-4 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${activeCategory === 'all' ? 'text-white shadow-md' : 'bg-gray-100/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400'}`}
                            style={activeCategory === 'all' ? { backgroundColor: 'var(--color-accent-500)' } : {}}
                        >Todos</button>
                    </div>
                </div>
            </div>

            {/* Product Grid */}
            <div className="px-4 py-6 space-y-3">
                {allProducts.length > 0 ? (
                    allProducts.map(({ product, menu }) => {
                        const isAvailable = isMenuTimeValid(menu);
                        const hasSubProducts = product.sub_products && product.sub_products.length > 0;
                        const totalQuantity = cart.filter(i => i.product.uuid === product.uuid).reduce((sum, i) => sum + i.quantity, 0);
                        return (
                            <div
                                key={product.uuid}
                                onClick={() => setSelectedProduct(product)}
                                className={`group bg-white dark:bg-gray-800/50 rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-white/5 flex cursor-pointer hover:shadow-md transition-all duration-300 ${!isAvailable ? 'opacity-60' : ''}`}
                            >
                                <div className="w-[110px] h-[110px] flex-shrink-0 relative overflow-hidden">
                                    <img
                                        src={getFullImageUrl(product.image)}
                                        alt={product.name}
                                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${!isAvailable ? 'grayscale-[0.6]' : ''}`}
                                    />
                                </div>
                                <div className="flex-1 p-4 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start gap-2">
                                            <h3 className="font-bold text-gray-900 dark:text-white text-sm line-clamp-1">{product.name}</h3>
                                            <span className="text-accent font-bold text-base shrink-0" style={{ color: 'var(--color-accent-500)' }}>{currencySymbol}{product.price ? product.price.toFixed(0) : '0'}</span>
                                        </div>
                                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1 line-clamp-2 whitespace-pre-line">{product.description}</p>
                                        {product.preparation_time && product.preparation_time !== '00:00:00' && (
                                            <div className="flex items-center gap-1 mt-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatPreparationTime(product.preparation_time)}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end mt-2" onClick={e => e.stopPropagation()}>
                                        {!hasSubProducts && totalQuantity > 0 ? (
                                            <div className="flex items-center gap-2 rounded-xl px-2 py-1" style={{ backgroundColor: 'var(--color-accent-500)' }}>
                                                <button onClick={() => updateQuantity(product.uuid, totalQuantity - 1)} className="text-white">
                                                    <Minus className="w-3.5 h-3.5" />
                                                </button>
                                                <span className="text-white font-bold text-sm w-4 text-center">{totalQuantity}</span>
                                                <button onClick={() => updateQuantity(product.uuid, totalQuantity + 1)} className="text-white">
                                                    <Plus className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    if (hasSubProducts) {
                                                        setSelectedProduct(product);
                                                    } else {
                                                        addItem(product, 1);
                                                    }
                                                }}
                                                disabled={!isAvailable}
                                                className="flex items-center gap-2 disabled:bg-gray-300 text-white rounded-xl px-3 py-1.5 text-xs font-bold transition-colors"
                                                style={!isAvailable ? {} : { backgroundColor: 'var(--color-accent-500)' }}
                                            >
                                                <div className="flex items-center gap-1">
                                                    <Plus className="w-3.5 h-3.5" /> 
                                                    {hasSubProducts && totalQuantity > 0 ? 'Agregar otro' : 'Agregar'}
                                                </div>
                                                {hasSubProducts && totalQuantity > 0 && (
                                                    <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[9px]">
                                                        {totalQuantity}
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="py-20 text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Sin resultados</h3>
                        <p className="text-gray-500 text-sm">Probá con otras palabras.</p>
                    </div>
                )}
            </div>

            {/* Smart Orders Banner */}
            {identifier && allOrders.length > 0 && (
                <div className="px-4 mt-2 -mb-2">
                    <button
                        onClick={() => navigate(`/simple-dashboard/${token}`)}
                        className="w-full relative overflow-hidden bg-white dark:bg-gray-800/60 backdrop-blur-xl rounded-[2rem] p-6 shadow-xl border border-gray-100 dark:border-gray-700/50 flex flex-col sm:flex-row items-center gap-5 text-left transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] active:scale-95 group"
                    >
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-accent-500/10 dark:bg-accent-500/5 rounded-full blur-3xl group-hover:bg-accent-500/20 transition-colors duration-500" style={{ backgroundColor: 'var(--color-accent-500)', opacity: 0.1 }} />
                        
                        <div className="relative shrink-0">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-accent-500 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:rotate-6 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, var(--color-primary-400), var(--color-accent-500))', boxShadow: '0 10px 15px -3px var(--color-accent-500)' }}>
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 border-2 border-white dark:border-gray-800 rounded-full flex items-center justify-center shadow-md animate-bounce">
                                <span className="text-white text-[11px] font-black">{allOrders.length}</span>
                            </div>
                        </div>
                        
                        <div className="flex-1 relative z-10 w-full text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1.5">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                                <span className="text-[11px] font-black text-green-600 dark:text-green-400 uppercase tracking-widest">En curso</span>
                            </div>
                            <h4 className="text-[22px] font-black text-gray-900 dark:text-white tracking-tight leading-none mb-2">Mis pedidos</h4>
                            <p className="text-[14px] text-gray-500 dark:text-gray-400 font-medium leading-tight">
                                Tienes {allOrders.length} pedido{allOrders.length > 1 ? 's' : ''} activo{allOrders.length > 1 ? 's' : ''}. Toca aquí para revisar el estado.
                            </p>
                        </div>
                    </button>
                </div>
            )}

            {/* About Us Section - Siempre visible al final */}
            {tenantAbout && (
                <div className="px-4 pb-32">
                    <AboutUsSection tenant={tenantAbout} />
                </div>
            )}

            {/* Product Detail Modal */}
            {selectedProduct && (
                <ProductDetailModal
                    product={selectedProduct}
                    isOpen={!!selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    onAdd={(prod, qty, opts) => addItem(prod, qty, opts)}
                    currencySymbol={currencySymbol}
                />
            )}

            {/* Offer Detail Modal */}
            {selectedOffer && (
                <OfferDetailModal
                    offer={selectedOffer}
                    isOpen={!!selectedOffer}
                    onClose={() => setSelectedOffer(null)}
                    onAddOffer={(offer) => addOffer(offer, 1)}
                    currencySymbol={currencySymbol}
                />
            )}

            {/* Cart FAB */}
            {totalItems > 0 && !cartOpen && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md z-50">
                    <button
                        onClick={() => setCartOpen(true)}
                        className="w-full btn-accent py-4 px-6 rounded-2xl font-bold shadow-2xl flex items-center justify-between transition-all"
                        style={{ boxShadow: '0 20px 25px -5px var(--color-accent-500)' }}
                    >
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <ShoppingCart className="w-5 h-5" />
                                <span className="absolute -top-2 -right-2 bg-white text-accent text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center" style={{ color: 'var(--color-accent-500)' }}>{totalItems}</span>
                            </div>
                            <span>{totalItems} producto{totalItems !== 1 ? 's' : ''}</span>
                        </div>
                        <span className="font-black text-lg">{currencySymbol}{totalPrice.toFixed(2)}</span>
                    </button>
                </div>
            )}

            {/* Cart Drawer */}
            {cartOpen && (
                <div className="fixed inset-0 z-[90] flex flex-col justify-end animate-in fade-in duration-200">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
                    <div className="relative bg-white dark:bg-[#161b2c] rounded-t-[2.5rem] w-full max-w-md mx-auto shadow-2xl border-t border-white/10 animate-in slide-in-from-bottom-5 duration-300 max-h-[80vh] flex flex-col">
                        {/* Drawer Handle */}
                        <div className="flex justify-center pt-3 pb-2">
                            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                            <h2 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5 text-accent" style={{ color: 'var(--color-accent-500)' }} />
                                Tu pedido
                            </h2>
                            <button onClick={() => setCartOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">
                                <ChevronUp className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Cart Items */}
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                            {cart.map((item) => (
                                <div key={item.cartItemId} className="flex flex-col gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-3 shadow-sm border border-gray-100 dark:border-gray-700/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={getFullImageUrl(item.product.image)}
                                            alt={item.product.name}
                                            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-sm truncate">{item.product.name}</h4>
                                            <p className="text-accent font-bold text-sm" style={{ color: 'var(--color-accent-500)' }}>
                                                {currencySymbol}
                                                {((item.product.price + (item.sub_products?.reduce((acc: number, sub: SubProduct) => acc + sub.additional_price, 0) || 0)) * item.quantity).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => updateQuantity(item.cartItemId || item.product.uuid, item.quantity - 1)} className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                                <Minus className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
                                            </button>
                                            <span className="w-6 text-center font-black text-gray-900 dark:text-white text-sm">{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.cartItemId || item.product.uuid, item.quantity + 1)} className="w-8 h-8 rounded-xl flex items-center justify-center hover:opacity-90 transition-colors shadow-sm" style={{ backgroundColor: 'var(--color-accent-500)' }}>
                                                <Plus className="w-3.5 h-3.5 text-white" />
                                            </button>
                                            <button onClick={() => removeFromCart(item.cartItemId || item.product.uuid)} className="w-8 h-8 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center ml-1 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                    {item.sub_products && item.sub_products.length > 0 && (
                                        <div className="flex flex-wrap gap-2 px-1 pb-1">
                                            {item.sub_products.map((sub: SubProduct) => (
                                                <div key={sub.sub_product_uuid} className="flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-lg border border-orange-100 dark:border-orange-800/50">
                                                    <div className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--color-accent-500)' }} />
                                                    <span className="text-[9px] font-black uppercase tracking-wider" style={{ color: 'var(--color-accent-600)' }}>{sub.sub_product_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {cartOffers.map((item) => (
                                <div key={item.offer.uuid} className="flex flex-col gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-3 shadow-sm border border-gray-100 dark:border-gray-700/50 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-sky-400 to-pink-500 flex items-center justify-center">
                                            {(item.offer.image_url || item.offer.image) ? (
                                                <img
                                                    src={getFullImageUrl(item.offer.image_url || item.offer.image || '')}
                                                    alt={item.offer.name}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                                />
                                            ) : (
                                                <Sparkles className="w-6 h-6 text-white" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-gray-900 dark:text-white text-sm truncate">{item.offer.name}</h4>
                                            <p className="text-accent font-bold text-sm" style={{ color: 'var(--color-accent-500)' }}>
                                                {currencySymbol}{(item.offer.price * item.quantity).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => updateOfferQuantity(item.offer.uuid, item.quantity - 1)} className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                                <Minus className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300" />
                                            </button>
                                            <span className="w-6 text-center font-black text-gray-900 dark:text-white text-sm">{item.quantity}</span>
                                            <button onClick={() => updateOfferQuantity(item.offer.uuid, item.quantity + 1)} className="w-8 h-8 rounded-xl flex items-center justify-center hover:opacity-90 transition-colors shadow-sm" style={{ backgroundColor: 'var(--color-accent-500)' }}>
                                                <Plus className="w-3.5 h-3.5 text-white" />
                                            </button>
                                            <button onClick={() => removeOffer(item.offer.uuid)} className="w-8 h-8 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center ml-1 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="px-6 pt-4 pb-8 border-t border-gray-100 dark:border-gray-800 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">Total</span>
                                <span className="text-2xl font-black text-gray-900 dark:text-white">{currencySymbol}{totalPrice.toFixed(2)}</span>
                            </div>
                            <button
                                onClick={handleProceedToPayment}
                                className="w-full btn-accent py-4 rounded-2xl font-bold text-base shadow-lg transition-all"
                                style={{ boxShadow: '0 10px 15px -3px var(--color-accent-500)' }}
                            >
                                Ir a Pagar →
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
